<template>
    <vx-expandable-card>
        <template v-slot:header>Require SSO</template>
        <template v-slot:content>
            <div class="flex flex-col gap-4">
                <div class="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <div class="font-bold">
                            Everyone except the team owner must log in with SSO to access this team.
                        </div>
                        <div class="text-sm text-slate-500">
                            Applies to the web app, personal access tokens and the mobile app. Invitations can then only
                            go to verified email domains.
                        </div>
                    </div>
                    <vx-toggle
                        :modelValue="props.requireSso"
                        @update:modelValue="toggle"
                        :loading="saving"
                        size="md"
                        data-test="require-sso-toggle"
                    />
                </div>

                <vx-alert v-if="props.requireSso" color="info" :closeable="false" data-test="require-sso-on">
                    Require SSO is on. The connection and the last verified domain cannot be removed or disabled until
                    it is turned off.
                </vx-alert>

                <vx-alert v-else-if="blocker" color="slate" :closeable="false" data-test="require-sso-blocker">
                    {{ conflictMessage[blocker] }}
                </vx-alert>

                <vx-alert
                    v-if="refusal"
                    color="danger"
                    data-test="require-sso-refusal"
                    @update:visible="refusal = null"
                >
                    {{ refusal }}
                </vx-alert>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { VxAlert, VxExpandableCard, VxSwal, VxToggle } from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import { pluralized } from '~/components/filters';
import { useToasted } from '~/composables/useToasted';
import { ssoClient } from '~/services';
import type { SsoConfig, SsoConflictCode } from '~/services/ssoClient.types';
import { catchRequestError } from '~/utils/request';
import { conflictMessage, conflictText } from './ssoSettingsHelpers';

// The config payload cannot tell a missing connection from a disabled one; the server's 409 covers the third code.
type RequireSsoBlocker = Extract<SsoConflictCode, 'connection_disabled' | 'no_verified_domain'>;

const props = defineProps<{
    teamId: number;
    config: SsoConfig;
    requireSso: boolean;
}>();

const emit = defineEmits<{
    updated: [config: SsoConfig];
}>();

const toasted = useToasted();
const saving = ref(false);
const refusal = ref<string | null>(null);

const blocker = computed<RequireSsoBlocker | null>(() => {
    if (!props.config.connection_enabled) {
        return 'connection_disabled';
    }

    return props.config.verified_domains_count === 0 ? 'no_verified_domain' : null;
});

// Only numbers and fixed words go into the html, so nothing needs escaping.
const impactHtml = () => {
    const impact = props.config.require_sso_impact;
    const lines = [
        `Everyone except you must log in with SSO to use this team (${pluralized(
            'member',
            impact.members_total,
            true
        )}).`,
    ];

    if (impact.members_without_sso_identity > 0) {
        lines.push(
            `<strong>${pluralized('member', impact.members_without_sso_identity, true)}</strong> ${
                impact.members_without_sso_identity === 1 ? 'has' : 'have'
            } never logged in with SSO.`
        );
    }

    if (impact.members_outside_verified_domains > 0) {
        lines.push(
            `<strong>${pluralized('member', impact.members_outside_verified_domains, true)}</strong> ${
                impact.members_outside_verified_domains === 1 ? 'has an email' : 'have emails'
            } outside your verified domains and will be locked out.`
        );
    }

    return lines.join('<br>');
};

const update = async (requireSso: boolean) => {
    saving.value = true;
    refusal.value = null;

    try {
        const { data } = await ssoClient.updateConfig(props.teamId, { require_sso: requireSso });
        emit('updated', data);
        toasted.success(requireSso ? 'Require SSO turned on.' : 'Require SSO turned off.');
    } catch (error) {
        refusal.value = conflictText(error);

        if (refusal.value === null) {
            toasted.error(catchRequestError(error));
        }
    } finally {
        saving.value = false;
    }
};

const toggle = (requireSso: boolean) => {
    if (!requireSso) {
        update(false);
        return;
    }

    if (blocker.value !== null) {
        refusal.value = conflictMessage[blocker.value];
        return;
    }

    VxSwal.fire({
        title: 'Require SSO?',
        icon: 'warning',
        html: impactHtml(),
        showCancelButton: true,
        confirmButtonText: 'Require SSO',
        confirmButtonColor: 'danger',
    }).then(({ value }) => {
        if (value) {
            update(true);
        }
    });
};
</script>
